import { useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'

/**
 * Logs the authenticated user out after a period of inactivity.
 *
 * Mounted once in the authenticated shell (components/layout/Layout.tsx). On
 * timeout it calls the real `logout()` — which clears the persisted store AND
 * hits the server logout / SSO-logout endpoint so a real JWT is revoked
 * server-side (a client-only clear would leave a still-valid token replayable) —
 * then hard-redirects to /login.
 *
 * Activity is shared across tabs via a throttled `localStorage` timestamp, so a
 * user active in one tab is not logged out of an idle sibling tab. The timer
 * re-checks the shared last-activity time rather than trusting a per-tab timer.
 *
 * Duration: VITE_IDLE_TIMEOUT_MINUTES (default 30). Set to 0 to disable.
 *
 * Scope note: the idle clock only accrues while the app is mounted — time spent
 * with the tab/browser closed is not counted (mount re-seeds the activity key).
 * This hook is the auto-logout for an unattended-but-open session; the hard
 * backstop for a persisted-then-replayed token is the server-side token TTL
 * (real JWT exp, or DevTokenTTL for dev-mode tokens), not this timer.
 */
const DEFAULT_IDLE_MINUTES = 30
const ACTIVITY_KEY = 'vharta-last-activity'
const WRITE_THROTTLE_MS = 5_000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const

function idleTimeoutMs(): number {
  const raw = import.meta.env.VITE_IDLE_TIMEOUT_MINUTES
  if (raw === undefined || raw === '') return DEFAULT_IDLE_MINUTES * 60_000
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return DEFAULT_IDLE_MINUTES * 60_000
  return n * 60_000 // 0 => disabled
}

export function useIdleTimeout(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    const timeoutMs = idleTimeoutMs()
    if (!isAuthenticated || timeoutMs <= 0) return

    let timer: number | undefined
    let lastWrite = 0

    const readLastActivity = (): number => {
      const v = Number(localStorage.getItem(ACTIVITY_KEY))
      return Number.isFinite(v) && v > 0 ? v : Date.now()
    }

    const markActivity = (): void => {
      const now = Date.now()
      if (now - lastWrite > WRITE_THROTTLE_MS) {
        lastWrite = now
        localStorage.setItem(ACTIVITY_KEY, String(now))
      }
    }

    const doLogout = (): void => {
      // Another tab may have already logged out — don't double-fire.
      if (!useAuthStore.getState().isAuthenticated) return
      // Navigate only after the server revoke settles: navigating aborts it.
      void useAuthStore
        .getState()
        .logout()
        .then(({ serverRevoked }) => {
          window.location.assign(serverRevoked ? '/login?reason=idle' : '/login?reason=signout_incomplete')
        })
    }

    const check = (): void => {
      const idleFor = Date.now() - readLastActivity()
      if (idleFor >= timeoutMs) {
        doLogout()
        return
      }
      // Not idle yet (activity in this or another tab) — re-check when the
      // current inactivity window would elapse.
      timer = window.setTimeout(check, timeoutMs - idleFor + 500)
    }

    // Seed activity so a freshly-mounted shell isn't treated as already idle.
    lastWrite = Date.now()
    localStorage.setItem(ACTIVITY_KEY, String(lastWrite))

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }))
    timer = window.setTimeout(check, timeoutMs)

    return () => {
      if (timer) window.clearTimeout(timer)
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity))
    }
  }, [isAuthenticated])
}
